<template>
  <card-dialog
    v-model="modalVisible"
    max-width="500px"
    :title="$t('button.filters')"
  >
    <v-card-text
      class="pa-6"
    >
      <subtitle>
        {{ $t("card_dialog.diagnostic_filters.player_event") }}
      </subtitle>
      <v-row class="mt-1">
        <v-col>
          <v-select
            v-model="dirtyFilters.eventId"
            :items="events.data"
            item-text="title"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :disabled="events.data.length === 0"
            :loading="events.loading"
            :label="$t('event.label.title')"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $dayjs(item.start_at).format('ll') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <subtitle class="mt-4">
        {{ $t("card_dialog.diagnostic_filters.reference_group") }}
      </subtitle>
      <v-alert
        text
        outlined
        color="primary"
        class="text-caption mt-2"
      >
        {{ $t("alert.diagnostic_data_sensitive") }}
      </v-alert>
      <v-row class="mt-2">
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.teamId"
            :items="teams"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :label="$t('dropdown.select_team.title')"
          />
        </v-col>
        <v-col cols="6">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="formattedRange"
                :label="$t('text_input.select_date_range.title')"
                readonly
                outlined
                clearable
                dense
                hide-details
                v-on="on"
                @click:clear="dirtyFilters.date = []"
              />
            </template>

            <v-date-picker
              v-model="dirtyFilters.date"
              :first-day-of-week="1"
              range
              no-title
              :max="$dayjs().format('YYYY-MM-DD')"
              @update:model-value="dateMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.sportId"
            :items="sports.data"
            :loading="sports.loading"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :label="$t('dropdown.select_sport.title')"
          />
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.rankId"
            :items="ranks.data"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :label="$t('dropdown.select_rank.title')"
          />
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.sex"
            :items="[{ id: 'M', name: $t('select.data.gender.male')}, { id: 'F', name: $t('select.data.gender.female')}]"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :label="$t('dropdown.sex.title')"
          />
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.countryCode"
            :items="countries"
            item-text="name"
            item-value="code"
            outlined
            dense
            hide-details
            clearable
            :label="$t('dropdown.competition.title')"
          />
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="dirtyFilters.positionId"
            :items="playerPositions.data"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            clearable
            :label="$t('individual_analysis.data.position')"
          />
        </v-col>
        <v-col cols="12">
          <v-range-slider
            v-model="dirtyFilters.age"
            color="primary"
            class="pt-10"
            thumb-color="primary"
            track-color="grey"
            thumb-label="always"
            :label="$t('individual_analysis.data.age')"
            :step="1"
            :min="1"
            :max="100"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4 grey-bg">
        <v-spacer />
        <v-btn
          color="primary"
          class="text-none"
          @click="$emit('click:apply'); modalVisible = false"
        >
          {{ $t('button.apply') }}
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>
<script>

import CardDialog from '@/components/helpers/CardDialog.vue'
import { SportApiService } from '@/data/services/SportApiService'
import { PlayerApiService, PlayerPositionApiService } from '@/data/services'
import { toCamelCase } from '@/utils/helpers'
import dayjs from 'dayjs'
import { countries } from '@/data/models/Countries'
import { EventType } from '@/data/models'
import Subtitle from '@/components/helpers/Subtitle.vue'

export default {
  name: 'DiagnosticFiltersDialog',
  components: { Subtitle, CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      required: true
    },
    player: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      sports: {
        loading: false,
        data: []
      },
      ranks: {
        loading: false,
        data: []
      },
      playerPositions: {
        loading: false,
        data: []
      },
      events: {
        loading: false,
        data: []
      },
      dateMenu: false,
      countries: countries
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    dirtyFilters: {
      get: function () {
        return this.filters
      },
      set: function (value) {
        this.$emit('update:filters', value)
      }
    },
    formattedRange() {
      if (this.filters.date.length === 2) {
        return `${dayjs(this.filters.date[0]).format('DD/MM')} - ${dayjs(this.filters.date[1]).format('DD/MM')}`
      }
      return ''
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchSports()
        this.fetchRanks()
        this.fetchPlayerPositions()
        this.fetchPlayerEvents()
      }
    }
  },
  created() {

  },
  methods: {
    async fetchSports() {
      try {
        this.sports.loading = true

        this.sports.data = await SportApiService.getAll()
        this.sports.data = this.sports.data.map(sport => {
          return {
            ...sport,
            name: this.$t(`sport.${sport.name.toLowerCase()}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.sports.loading = false
      }
    },
    async fetchRanks() {
      try {
        this.ranks.loading = true

        this.ranks.data = await SportApiService.getAllRanks()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.ranks.loading = false
      }
    },
    async fetchPlayerPositions() {
      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerEvents() {
      if (!this.player.id) return

      try {
        this.events.loading = true

        const response = await PlayerApiService.getEvents(this.player.id, {
          types: [EventType.TEST_SESSION, EventType.READINESS]
        })

        this.events.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.events.loading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
